import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/format";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export default function Control() {
  const [data, setData] = useState({ orders: [], regels: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/analytics/control")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (ordernummer, status) => {
    await api.post(`/orders/${ordernummer}/controle-status`, null, { params: { status } });
    toast.success(`Order ${ordernummer} gemarkeerd als ${status}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="control-page">
      <header>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Controle</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Controle nodig</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Orders met ontbrekende inkoopprijzen, niet-gemapte betaalmethodes of andere afwijkingen.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-amber-50 border-amber-200 p-5 rounded-lg" data-testid="control-orders-count">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-amber-700">Orders met controle</p>
          <p className="text-3xl font-semibold text-amber-900 mt-1">{data.orders.length}</p>
        </Card>
        <Card className="bg-amber-50 border-amber-200 p-5 rounded-lg" data-testid="control-regels-count">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-amber-700">Orderregels met controle</p>
          <p className="text-3xl font-semibold text-amber-900 mt-1">{data.regels.length}</p>
        </Card>
      </div>

      <Tabs defaultValue="orders" data-testid="control-tabs">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-control-orders">Orders ({data.orders.length})</TabsTrigger>
          <TabsTrigger value="regels" data-testid="tab-control-regels">Orderregels ({data.regels.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="orders">
          <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="control-orders-table">
                <thead>
                  <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                    <th className="text-left py-3 px-4 font-semibold">Ordernr</th>
                    <th className="text-left py-3 px-4 font-semibold">Datum</th>
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-left py-3 px-4 font-semibold">Klant</th>
                    <th className="text-right py-3 px-4 font-semibold">Omzet</th>
                    <th className="text-left py-3 px-4 font-semibold">Reden</th>
                    <th className="text-right py-3 px-4 font-semibold">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="7" className="text-center text-slate-400 py-8">Laden…</td></tr>}
                  {!loading && data.orders.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-emerald-600 py-12 text-sm font-medium">
                      <CheckCircle2 className="inline-block h-5 w-5 mr-1.5 -mt-0.5" />
                      Geen orders met controle. Alles helder ✨
                    </td></tr>
                  )}
                  {data.orders.map((o) => (
                    <tr key={o.ordernummer} className="border-b border-slate-100 hover:bg-amber-50/30">
                      <td className="py-2.5 px-4 font-medium text-slate-900">{o.ordernummer}</td>
                      <td className="py-2.5 px-4 text-slate-600">{formatDate(o.datum)}</td>
                      <td className="py-2.5 px-4">
                        <Badge variant="outline" className="bg-slate-50">{o.orderstatus}</Badge>
                      </td>
                      <td className="py-2.5 px-4 text-slate-700">{o.voornaam} {o.achternaam}</td>
                      <td className="py-2.5 px-4 text-right">{formatCurrency(o.prijs_incl_btw)}</td>
                      <td className="py-2.5 px-4">
                        <div className="flex flex-col gap-1">
                          {(o.controle_redenen || []).map((r, i) => (
                            <span key={`reden-${i}-${r.slice(0,20)}`} className="text-xs text-amber-800 flex items-start gap-1">
                              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                              {r}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" className="text-emerald-700 h-8 px-2" onClick={() => updateStatus(o.ordernummer, "opgelost")} data-testid={`control-resolve-${o.ordernummer}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Opgelost
                          </Button>
                          <Button size="sm" variant="ghost" className="text-slate-500 h-8 px-2" onClick={() => updateStatus(o.ordernummer, "genegeerd")} data-testid={`control-ignore-${o.ordernummer}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1" /> Negeer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="regels">
          <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="control-regels-table">
                <thead>
                  <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                    <th className="text-left py-3 px-4 font-semibold">Ordernr</th>
                    <th className="text-left py-3 px-4 font-semibold">Product</th>
                    <th className="text-right py-3 px-4 font-semibold">Aantal</th>
                    <th className="text-left py-3 px-4 font-semibold">Reden</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && data.regels.length === 0 && (
                    <tr><td colSpan="4" className="text-center text-emerald-600 py-12 text-sm font-medium">
                      <CheckCircle2 className="inline-block h-5 w-5 mr-1.5 -mt-0.5" />
                      Geen problematische orderregels.
                    </td></tr>
                  )}
                  {data.regels.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="py-2.5 px-4 font-medium text-slate-900">{r.ordernummer}</td>
                      <td className="py-2.5 px-4 text-slate-700">
                        <div>{r.productnaam}</div>
                        <div className="text-xs text-slate-500">{r.productnummer}</div>
                      </td>
                      <td className="py-2.5 px-4 text-right">{r.aantal}</td>
                      <td className="py-2.5 px-4 text-xs text-amber-800">
                        {(r.controle_redenen || []).join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
